import { Trans } from "@lingui/react/macro"
import {
  IconChecklist,
  IconFileText,
  IconGavel,
  IconHash,
  IconHelpCircle,
  IconMessageCircle,
  IconQuote,
} from "@tabler/icons-react"
import type { TSearchChannel } from "../lib/types/search"

/**
 * Per-channel presentation: a semantic tone (drives the card / chip `data-tone`
 * accent — never a literal palette name, see theming rule #6) and an icon.
 * Tones map to meaning, not hue: transcript/quotes = brand (core speech),
 * decisions = success (settled), action points = warning (to-do), open
 * questions = highlight (unresolved emphasis), summary/topics = info (derived).
 */
export type TChannelTone = "brand" | "info" | "success" | "warning" | "highlight"

interface IChannelMeta {
  tone: TChannelTone
  Icon: React.ComponentType<{ size?: number | string }>
}

export const CHANNEL_META: Record<TSearchChannel, IChannelMeta> = {
  transcript: { tone: "brand", Icon: IconMessageCircle },
  summary: { tone: "info", Icon: IconFileText },
  decisions: { tone: "success", Icon: IconGavel },
  action_points: { tone: "warning", Icon: IconChecklist },
  open_questions: { tone: "highlight", Icon: IconHelpCircle },
  topics: { tone: "info", Icon: IconHash },
  speaker_quotes: { tone: "brand", Icon: IconQuote },
}

/** Fallback for an unexpected wire channel — treated as a neutral info result. */
const FALLBACK_META: IChannelMeta = { tone: "info", Icon: IconFileText }

export function channelMeta(sourceChannel: string): IChannelMeta {
  return (CHANNEL_META as Record<string, IChannelMeta | undefined>)[sourceChannel] ?? FALLBACK_META
}

/**
 * Human label per channel. A switch returning literal `<Trans>` (not a dynamic
 * key) so Lingui can extract every string — see the translations skill.
 */
export const ChannelLabel = ({ sourceChannel }: { sourceChannel: string }): React.JSX.Element => {
  switch (sourceChannel) {
    case "transcript":
      return <Trans>Transcript</Trans>
    case "summary":
      return <Trans>Summary</Trans>
    case "decisions":
      return <Trans>Decision</Trans>
    case "action_points":
      return <Trans>Action point</Trans>
    case "open_questions":
      return <Trans>Open question</Trans>
    case "topics":
      return <Trans>Topic</Trans>
    case "speaker_quotes":
      return <Trans>Quote</Trans>
    default:
      return <Trans>Result</Trans>
  }
}
